import React, {
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import FilePresentIcon from '@mui/icons-material/FilePresentRounded';
import DeleteIcon from 'core/icons/delete';
import EditIcon from 'core/icons/edit';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import ZoomIcon from 'core/icons/zoom';
import useDeleteMediaHandler, {
  RequestMultiMediaDelete,
} from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import {
  MultimediaType,
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import DownloadIcon from 'core/icons/download';
import Button from 'pages/Dashboard/components/Button';
import {
  useGetAllowedFileTypes,
  useGetMediaPreviewInfo,
} from 'pages/Dashboard/hooks/media';
import Spinner from 'pages/Dashboard/components/Spinner';
import UploadIcon from 'core/icons/upload';
import FilePicker, {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  usePatientGetMultimediaByMedia,
} from 'views/EMR/hooks/media';
import head from 'lodash/head';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useMixpanelTrack,
} from 'core/hooks';
import PdfMediaPreview from 'pages/Dashboard/components/PdfMediaPreview';
import {
  showCustomPreview,
} from 'utils/device';

type Parts = 'root' | 'image';

type Props = {
  media: MultiMediaResponse;
  isFullScreen?: boolean;
  zoomable?: boolean;
  fullSize?: boolean;
  hasDelete?: boolean;
  thumbnail?: boolean;
  className?: string;
  onDelete?: (meta: RequestMultiMediaDelete) => void | Promise<void>;
  onUpdate?: (files: FileLocalUrl[]) => void;
  onEdit?: (files: FileLocalUrl[]) => void;
  mediaType?: MultimediaType;
  classes?: Partial<Record<Parts, string>>;
  coverImage?: boolean;
};

type ThumbnailToolbarProps = Pick<Props, 'media' | 'onDelete' | 'onUpdate' | 'onEdit' | 'hasDelete' | 'mediaType'>;

function ThumbnailToolbar({
  media,
  onDelete: _onDelete,
  hasDelete = true,
  onUpdate,
  onEdit,
  mediaType,
}: ThumbnailToolbarProps) {
  const mixpanelTrack = useMixpanelTrack();
  const onPreviewClick = usePreviewMediaClick();
  const { onMediaDelete } = useDeleteMediaHandler();
  const onDelete = typeof _onDelete === 'function' ? _onDelete : onMediaDelete;
  const allowedFileTypes = useGetAllowedFileTypes(mediaType);

  return (
    <div className={clsx(
      'absolute transition-opacity opacity-0 inset-x-0 top-0 !h-8 w-full',
      'group-hover/thumbnail:opacity-100 group-hover/thumbnail:bg-black group-hover/thumbnail:bg-opacity-40',
    )}
    >
      <div className="flex gap-2 h-full items-center !w-full cursor-pointer justify-between p-2">
        <div className="!text-left text-white text-xs font-semibold truncate">
          {media?.fileName}
        </div>
        <div className="flex justify-center items-center gap-2">
          {!isNil(onUpdate) && (
            <ToolTip title="Update File">
              <span>
                <FilePicker onFileUpload={onUpdate} allowedFileTypes={allowedFileTypes}>
                  <UploadIcon className="!text-base !text-white !empty-fill" />
                </FilePicker>
              </span>
            </ToolTip>
          )}
          {!isNil(onEdit) && (
            <ToolTip title="Edit Image">
              <span>
                <EditIcon
                  onClick={(e: MouseEvent<SVGSVGElement>) => {
                    e.stopPropagation();
                    mixpanelTrack('onOpenImageEditorDialog');
                    mediaEventChannel.emit('onOpenImageEditorDialog', {
                      src: media?.url?.value ?? '',
                      fileName: media?.fileName ?? 'image.png',
                      onSave: (edited: FileLocalUrl) => {
                        if (onEdit) onEdit([edited]);
                        else if (onUpdate) onUpdate([edited]);
                      },
                    });
                  }}
                  className="!text-lg !text-white !empty-fill"
                />
              </span>
            </ToolTip>
          )}
          <ToolTip title="Open Preview">
            <span>
              <ZoomIcon
                onClick={() => { onPreviewClick(media); }}
                className="!text-lg !text-white !empty-fill"
              />
            </span>
          </ToolTip>
          {hasDelete && (
            <ToolTip title="Delete">
              <span>
                <DeleteIcon
                  onClick={() => {
                    onDelete({
                      list: [
                        {
                          originatorType: MediaOriginatorType.Patient,
                          mediaId: media?.mediaId ?? 0,
                          fileName: media?.fileName,
                        },
                      ],
                    });
                  }}
                  className="!w-5 !h-5 !text-white"
                />
              </span>
            </ToolTip>
          )}
        </div>
      </div>
    </div>
  );
}

const rootClasses = 'group/thumbnail w-full h-full relative flex flex-col justify-center items-center cursor-pointer';
const rootClassesDesktop = clsx(rootClasses, 'overflow-hidden');

export default function MediaPreview({
  media: _media,
  isFullScreen = false,
  thumbnail = false,
  className,
  fullSize = false,
  zoomable = false,
  classes,
  coverImage = true,
  ...props
}: Props) {
  const { data, isLoading: _loading } = usePatientGetMultimediaByMedia(_media);

  const media = useMemo(() => (
    isNil(_media?.url) ? head(data?.list) : _media
  ), [_media, data]) as MultiMediaResponse;

  const [zoomedIn, setZoomIn] = useState<boolean>(zoomable);

  const onZoom = useCallback(() => {
    setZoomIn(!zoomedIn);
  }, [zoomedIn]);

  const {
    src,
    content,
    previewAvailable,
    previewImage,
    previewPdf,
    PreviewTag,
    renderAsText,
    isFetching,
  } = useGetMediaPreviewInfo({ media, thumbnail });

  const isLoading = useMemo(
    () => _loading || isFetching,
    [isFetching, _loading],
  );

  const isCoverImage = useMemo(() => thumbnail && coverImage, [thumbnail, coverImage]);
  const canDownload = useMemo(
    () => !isNil(media?.mediaId) || notEmptyString(media?.url?.value ?? ''),
    [media?.mediaId, media?.url?.value],
  );
  const shouldShowCustomPreview = previewPdf && showCustomPreview();

  return (
    <div className={clsx(
      shouldShowCustomPreview ? rootClasses : rootClassesDesktop,
      className,
      {
        '!items-start': fullSize && !previewPdf,
        '!overflow-auto': shouldShowCustomPreview ? (zoomedIn && !previewPdf) : zoomedIn,
        '!overflow-hidden': shouldShowCustomPreview && (previewPdf || !zoomedIn),
      },
    )}
    >
      {shouldShowCustomPreview ? (
        <PdfMediaPreview
          fileUrl={media?.url?.value ?? ''}
          fileName={media?.fileName ?? undefined}
          thumbnail={thumbnail}
          canDownload={canDownload}
        />
      ) : (
        <PreviewTag
          key={src}
          title={media?.fileName ?? ''}
          className={clsx('border-none', {
            '!w-full h-full': !previewImage,
            '!w-full !h-auto': previewImage && !fullSize,
            '!h-auto !w-auto !max-w-full !max-h-full': fullSize && previewImage,
            'cursor-zoom-in': zoomable && !zoomedIn,
            'cursor-zoom-out !overflow-auto': zoomable && zoomedIn,
          })}
          classes={{
            root: clsx(classes?.root, {
              '!max-w-none !max-h-none': zoomedIn,
              'flex flex-col !justify-start': fullSize,
            }),
            img: clsx(classes?.image, {
              '!max-w-none !max-h-none': zoomedIn,
              '!object-contain': !isCoverImage,
              '!object-cover': isCoverImage,
              '!w-full !h-auto': thumbnail,
            }),
          }}
          src={src}
          variant="rounded"
          size="fit"
          onClick={zoomable ? onZoom : noop}
          color={thumbnail ? '#9ca3af' : 'transparent'}
        >
          {(thumbnail && isEmptyString(src ?? ''))
            ? (
              <div className="flex flex-col items-center p-2">
                <FilePresentIcon className="text-white !w-12 !h-12" />
                <p className="text-center text-xs">{media?.fileName}</p>
              </div>
            )
            : (
              <div className={clsx('w-full h-full flex flex-col overflow-hidden', {
                'justify-center items-center text-center': !previewAvailable,
              })}
              >
                {!isLoading && renderAsText && (
                  <div
                    className={clsx('flex w-full flex-1 rounded overflow-y-auto p-4', {
                      'justify-center items-center text-center': isFetching,
                    })}
                  >
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">
                      {content}
                    </pre>
                  </div>
                )}
                {!isLoading && !previewAvailable && (
                  <p className="p-4 text-gray-500 text-sm">
                    Preview is not available for this document.
                  </p>
                )}
                {isLoading
                  ? (<Spinner size={32} />)
                  : (
                    <div className={clsx({ 'p-4 border-t border-t-gray-300': renderAsText })}>
                      {canDownload && (
                        <Button
                          variant="contained"
                          color="primary"
                          href={media?.url?.value ?? ''}
                        >
                          <DownloadIcon className="mr-2 !w-4 !h-4 text-white" />
                          Download
                        </Button>
                      )}
                    </div>
                  )}
              </div>
            )}
        </PreviewTag>
      )}
      {thumbnail && (
        <ThumbnailToolbar
          {...props}
          media={media}
        />
      )}
    </div>
  );
}
